import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay } from "date-fns";

interface MetricsFilters {
  productId?: string;
  startDate: Date;
  endDate: Date;
}

interface MetricsData {
  mrr: number;
  arr: number;
  churn: number;
  totalRevenue: number;
  cac: number;
  ltv: number;
  averageTicket: number;
  totalCustomers: number;
  mrrChange: number;
  arrChange: number;
  churnChange: number;
  revenueChange: number;
  cacChange: number;
  ltvChange: number;
  averageTicketChange: number;
}

export const useMetrics = (filters: MetricsFilters) => {
  return useQuery({
    queryKey: ["metrics", filters.productId, filters.startDate, filters.endDate],
    queryFn: async (): Promise<MetricsData> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: userRole } = await supabase
        .from("user_roles")
        .select("tenant_id")
        .eq("user_id", user.id)
        .single();

      if (!userRole) throw new Error("No tenant found");

      const startDateStr = startOfDay(filters.startDate).toISOString();
      const endDateStr = endOfDay(filters.endDate).toISOString();

      // Query para métricas do período atual
      let query = supabase
        .from("metrics")
        .select("*")
        .eq("tenant_id", userRole.tenant_id)
        .gte("period_start", startDateStr)
        .lte("period_end", endDateStr);

      if (filters.productId) {
        query = query.eq("product_id", filters.productId);
      }

      const { data: metrics, error } = await query;
      if (error) throw error;

      // Calcular período anterior (mesmo tamanho do período atual)
      const periodDays = Math.ceil((filters.endDate.getTime() - filters.startDate.getTime()) / (1000 * 60 * 60 * 24));
      const previousStartDate = new Date(filters.startDate);
      previousStartDate.setDate(previousStartDate.getDate() - periodDays);
      const previousEndDate = new Date(filters.endDate);
      previousEndDate.setDate(previousEndDate.getDate() - periodDays);

      // Query para métricas do período anterior
      let previousQuery = supabase
        .from("metrics")
        .select("*")
        .eq("tenant_id", userRole.tenant_id)
        .gte("period_start", startOfDay(previousStartDate).toISOString())
        .lte("period_end", endOfDay(previousEndDate).toISOString());

      if (filters.productId) {
        previousQuery = previousQuery.eq("product_id", filters.productId);
      }

      const { data: previousMetrics } = await previousQuery;

      // Buscar total de clientes
      let customersQuery = supabase
        .from("customers")
        .select("id", { count: "exact" })
        .eq("tenant_id", userRole.tenant_id)
        .eq("is_active", true);

      if (filters.productId) {
        customersQuery = customersQuery.eq("product_id", filters.productId);
      }

      const { count: totalCustomers } = await customersQuery;

      // Calcular métricas
      const mrrMetrics = metrics?.filter(m => m.metric_type === "mrr") || [];
      const churnMetrics = metrics?.filter(m => m.metric_type === "churn") || [];
      const revenueMetrics = metrics?.filter(m => m.metric_type === "revenue") || [];
      const cacMetrics = metrics?.filter(m => m.metric_type === "cac") || [];
      const ltvMetrics = metrics?.filter(m => m.metric_type === "ltv") || [];

      const mrr = mrrMetrics.reduce((sum, m) => sum + Number(m.value), 0);
      const arr = mrr * 12;
      const churn = churnMetrics.length > 0 
        ? churnMetrics.reduce((sum, m) => sum + Number(m.value), 0) / churnMetrics.length 
        : 0;
      const totalRevenue = revenueMetrics.reduce((sum, m) => sum + Number(m.value), 0);
      const cac = cacMetrics.length > 0 
        ? cacMetrics.reduce((sum, m) => sum + Number(m.value), 0) / cacMetrics.length 
        : 0;
      const ltv = ltvMetrics.length > 0 
        ? ltvMetrics.reduce((sum, m) => sum + Number(m.value), 0) / ltvMetrics.length 
        : 0;
      const averageTicket = totalCustomers && totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

      // Calcular métricas do período anterior
      const previousMrrMetrics = previousMetrics?.filter(m => m.metric_type === "mrr") || [];
      const previousChurnMetrics = previousMetrics?.filter(m => m.metric_type === "churn") || [];
      const previousRevenueMetrics = previousMetrics?.filter(m => m.metric_type === "revenue") || [];
      const previousCacMetrics = previousMetrics?.filter(m => m.metric_type === "cac") || [];
      const previousLtvMetrics = previousMetrics?.filter(m => m.metric_type === "ltv") || [];

      const previousMrr = previousMrrMetrics.reduce((sum, m) => sum + Number(m.value), 0);
      const previousChurn = previousChurnMetrics.length > 0 
        ? previousChurnMetrics.reduce((sum, m) => sum + Number(m.value), 0) / previousChurnMetrics.length 
        : 0;
      const previousRevenue = previousRevenueMetrics.reduce((sum, m) => sum + Number(m.value), 0);
      const previousArr = previousMrr * 12;
      const previousCac = previousCacMetrics.length > 0 
        ? previousCacMetrics.reduce((sum, m) => sum + Number(m.value), 0) / previousCacMetrics.length 
        : 0;
      const previousLtv = previousLtvMetrics.length > 0 
        ? previousLtvMetrics.reduce((sum, m) => sum + Number(m.value), 0) / previousLtvMetrics.length 
        : 0;
      
      // Buscar total de clientes do período anterior
      let previousCustomersQuery = supabase
        .from("customers")
        .select("id", { count: "exact" })
        .eq("tenant_id", userRole.tenant_id)
        .eq("is_active", true)
        .gte("created_at", startOfDay(previousStartDate).toISOString())
        .lte("created_at", endOfDay(previousEndDate).toISOString());

      if (filters.productId) {
        previousCustomersQuery = previousCustomersQuery.eq("product_id", filters.productId);
      }

      const { count: previousTotalCustomers } = await previousCustomersQuery;
      const previousAverageTicket = previousTotalCustomers && previousTotalCustomers > 0 
        ? previousRevenue / previousTotalCustomers 
        : 0;

      // Calcular variações percentuais
      const mrrChange = previousMrr > 0 ? ((mrr - previousMrr) / previousMrr) * 100 : 0;
      const arrChange = previousArr > 0 ? ((arr - previousArr) / previousArr) * 100 : 0;
      const churnChange = previousChurn > 0 ? ((churn - previousChurn) / previousChurn) * 100 : 0;
      const revenueChange = previousRevenue > 0 ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;
      const cacChange = previousCac > 0 ? ((cac - previousCac) / previousCac) * 100 : 0;
      const ltvChange = previousLtv > 0 ? ((ltv - previousLtv) / previousLtv) * 100 : 0;
      const averageTicketChange = previousAverageTicket > 0 ? ((averageTicket - previousAverageTicket) / previousAverageTicket) * 100 : 0;

      return {
        mrr,
        arr,
        churn,
        totalRevenue,
        cac,
        ltv,
        averageTicket,
        totalCustomers: totalCustomers || 0,
        mrrChange,
        arrChange,
        churnChange,
        revenueChange,
        cacChange,
        ltvChange,
        averageTicketChange,
      };
    },
  });
};
